import type { ComponentProps, PropsWithChildren } from 'react';
import { memo, useDeferredValue, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import styled from 'styled-components';
import type { ApolloError } from '@apollo/client';
import { getLink } from '@frameio/shared/routing';
import { ListLinkWithPrefetch } from '@frameio/shared/routing/components/ListLinkWithPrefetch';
import { groupByDateFormatted } from '@frameio/shared/utils/groupByDateFormatted';
import {
  List,
  ListItem,
  ListSection,
  RouterProvider,
  theme,
} from '@frameio/vapor';
import type { TransferBatch } from '../../types/uploader';
import { getIncompleteJobs } from '../../utils';
import { NoUploads } from '../NoUploads';
import { TotalProgressBar } from '../TotalProgressBar';
import TEXT from './generated/TEXT/UploaderSheetList';
import {
  FIXED_FOOTER_HEIGHT,
  TransferSheetFooterLayout,
} from './UploaderSheetFooterLayout';
import { UploaderSheetListItemContent } from './UploaderSheetListItemContent';

TEXT.DEFINE({
  /** Aria label for the transfers list */
  transfersListAriaLabel: 'Transfers',
});

type UploaderSheetListProps = {
  error?: ApolloError;
  loading?: boolean;
  batches?: TransferBatch[];
  localJobs?: TransferBatch[];
};

function UploaderSheetList({
  error,
  batches,
  loading,
  localJobs,
}: UploaderSheetListProps) {
  let loadingState: ComponentProps<typeof List>['loading'];

  if (loading || !batches) {
    loadingState = 'loading';
  } else if (error) {
    loadingState = 'loading-error';
  }

  // groupByDateFormatted uses TEXT which internally calls React hooks,
  // so it must be called at render level (not inside useMemo)
  const groups = batches ? groupByDateFormatted(batches, 'insertedAt') : {};
  const items = Object.entries(groups).map(([date, transfers]) => ({
    dateLabel: date,
    id: date,
    items: transfers,
  }));

  const incompleteJobs = useMemo(() => {
    if (!localJobs) {
      return [];
    }
    return getIncompleteJobs(localJobs);
  }, [localJobs]);

  const deferredIncompleteJobs = useDeferredValue(incompleteJobs);

  const showBottomProgressBar = deferredIncompleteJobs.length > 0;

  return (
    <Container>
      <ListLinkProvider>
        <List
          animate={true}
          aria-label={TEXT.transfersListAriaLabel}
          data-testid="uploader-sheet-list"
          defaultSize={800}
          estimatedItemHeight={64}
          itemGap={8}
          items={items}
          loading={loadingState}
          paddingBottom={showBottomProgressBar ? FIXED_FOOTER_HEIGHT : 8}
          renderLoading={(state) => {
            if (state === 'loading-error') {
              return <NoUploads />;
            }

            return null;
          }}
          renderPlaceholder={() => <NoUploads />}
          scrollPadding={{
            top: 0,
            bottom: showBottomProgressBar ? FIXED_FOOTER_HEIGHT : 8,
          }}
          variant="list"
        >
          {(section) => {
            return (
              <ListSection
                headerBackground={theme.colors.components.sheet.background}
                isHeaderSticky={true}
                isHeaderVisible={true}
                items={section.items}
                title={section.dateLabel}
              >
                {(item) => {
                  const projectId = item.targetFolder?.project?.id;
                  const folderId = item.targetFolder?.id;
                  const href = projectId
                    ? getLink({
                        page: 'folder',
                        projectId,
                        ...(folderId && { folderAssetId: folderId }),
                      })
                    : undefined;

                  return (
                    <ListItem href={href}>
                      <UploaderSheetListItemContent
                        job={item}
                        pathToFile={href}
                      />
                    </ListItem>
                  );
                }}
              </ListSection>
            );
          }}
        </List>
      </ListLinkProvider>
      <TransferSheetFooterLayout isShowing={showBottomProgressBar}>
        <TotalProgressBar incompleteJobs={deferredIncompleteJobs} />
      </TransferSheetFooterLayout>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  position: relative;
`;

function ListLinkProvider(props: PropsWithChildren) {
  const router = useRouter();
  return (
    <RouterProvider
      LinkComponent={ListLinkWithPrefetch as unknown as React.ComponentType}
      navigate={router.push}
      {...props}
    />
  );
}

const UploaderSheetList_ = memo(UploaderSheetList);
UploaderSheetList_.displayName = 'UploaderSheetList';
export { UploaderSheetList_ as UploaderSheetList };
